import { useId, useRef, type ReactNode } from 'react'
import { motion } from 'framer-motion'
import { SPRING_SWITCH } from '../tokens/motion'
import { cn } from '../lib/cn'
import { titleCase } from '../lib/titleCase'

export interface SegmentedOption<T extends string | number> {
  value: T
  label: string
  icon?: ReactNode
  /** A count shown after the label. */
  count?: number
}

/** A compact peer selector; form fields can request equal-width options. */
export function Segmented<T extends string | number>({
  value,
  options,
  onChange,
  label,
  className,
  fullWidth = false,
  size = 'md',
  hideLabels = false,
  variant = 'boxed',
}: {
  value: T
  options: readonly SegmentedOption<T>[]
  onChange: (value: T) => void
  label: string
  className?: string
  fullWidth?: boolean
  size?: 'sm' | 'md'
  /** Icon-only control: names stay for screen readers, sight sees glyphs. */
  hideLabels?: boolean
  /**
   * `boxed` is the control on a form, wearing its container. `plain` is the
   * same choice where the surroundings are already the container — glyphs in
   * a row, the chosen one inked, nothing behind them.
   */
  variant?: 'boxed' | 'plain'
}) {
  const group = useId()
  const buttons = useRef<(HTMLButtonElement | null)[]>([])
  const boxed = variant === 'boxed'
  return (
    <div role="radiogroup" aria-label={label} className={cn('wf-segmented', !boxed && 'wf-segmented-plain', fullWidth && 'wf-segmented-full', size === 'sm' && 'wf-segmented-compact', className)}>
      {options.map((option, index) => {
        const selected = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            ref={(node) => { buttons.current[index] = node }}
            tabIndex={selected || !options.some((entry) => entry.value === value) && index === 0 ? 0 : -1}
            onKeyDown={(event) => {
              const direction = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1
                : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 0
              if (!direction && event.key !== 'Home' && event.key !== 'End') return
              event.preventDefault()
              const next = event.key === 'Home' ? 0 : event.key === 'End' ? options.length - 1
                : (index + direction + options.length) % options.length
              onChange(options[next]!.value)
              buttons.current[next]?.focus()
            }}
            aria-checked={selected}
            onClick={() => onChange(option.value)}
            className={cn(
              'wf-segmented-option relative inline-flex items-center gap-2 text-ui font-medium',
              'whitespace-nowrap transition-colors duration-fast',
              selected ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {/* The choice is a fill, never a second box: the container keeps
                the one edge, and the chosen option lifts on a wash of the
                foreground instead of drawing its own border and shadow. */}
            {selected && boxed ? (
              <motion.span
                layoutId={`${group}-thumb`}
                transition={SPRING_SWITCH}
                className="absolute inset-0 rounded-xs bg-selected"
              />
            ) : null}
            <span className="relative inline-flex items-center gap-1.5">
              {option.icon}
              {hideLabels ? <span className="sr-only">{option.label}</span> : titleCase(option.label)}
              {option.count !== undefined ? (
                <span className="tabular-nums text-muted-foreground">{option.count}</span>
              ) : null}
            </span>
          </button>
        )
      })}
    </div>
  )
}
